import { useEffect, useState, useCallback } from "react";

import { parseGid, useCart } from "@shopify/hydrogen-react";
import { type CartLineInput } from "@shopify/hydrogen-react/storefront-api-types";

import { useLegacyCart } from "@cart/LegacyCartContext";
import { STOREFRONT_CART_ATTRIBUTES } from "@root/tablescape/constants";
import { useSectionSettings } from "@sections/Root/SectionSettingsContext";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import {
  type CartLineWithAttributeInput,
  type TablescapeState,
  type CartSectionSettings,
} from "@tablescape/state";
import {
  addIntelligemsLineItemAttribute,
  getShopifyGlobalId,
} from "@tablescape/utilities";

/**
 * Custom hook for managing adding items to the cart in Shopify.
 */
export function useAddToCartManager() {
  const [linesToAdd, setLinesToAdd] = useState<CartLineInput[] | undefined>();
  const { cartCreate, id, lines, linesAdd, status } = useCart();
  const {
    addToLegacyCart,
    clearLegacyCart,
    legacyCartAttributes,
    legacyCartItems,
    legacyCartStatus,
    updateToLegacyCart,
  } = useLegacyCart();

  const {
    settings: {
      baseDrawer: { intelligemsEnabled },
    },
  } = useSectionSettings<CartSectionSettings>();

  /**
   * This is the single most important function in the Cart scope
   * It is responsible for adding items to the cart
   * Be very careful when modifying this function
   * It is used by the CartDrawer, CartProductVariantDrawer, and CartGiftWithPurchaseDrawer
   */
  const handleAddToCart = useCallback(
    (data?: TablescapeState) => {
      const bundleProduct = data?.cart?.bundleProduct;
      const lines = data?.cart?.lines;

      if (!Array.isArray(lines)) {
        return;
      }

      const validLines = lines.filter(
        (line): line is CartLineWithAttributeInput => !!(line && line?.id)
      );

      const bundleTimestamp = Date.now();
      const isStandaloneBundleKit = validLines.every((line) =>
        line.attributes.find(
          (attribute) =>
            attribute.key === STOREFRONT_CART_ATTRIBUTES.STANDALONE_BUNDLE_KIT
        )
      );

      const linesToAdd = validLines.map((line, index) => {
        const attributes = intelligemsEnabled
          ? addIntelligemsLineItemAttribute(line.id, line.attributes)
          : line.attributes;

        return {
          attributes: [
            ...(attributes ?? []),
            ...(bundleProduct && !isStandaloneBundleKit
              ? [
                  {
                    key: STOREFRONT_CART_ATTRIBUTES.TIMESTAMP,
                    value: String(bundleTimestamp),
                  },
                ]
              : []),
            ...(bundleProduct && !isStandaloneBundleKit && index === 0
              ? [
                  {
                    key: STOREFRONT_CART_ATTRIBUTES.RELATED_ITEMS,
                    value: validLines
                      .map((line) =>
                        line.id.toString().includes("gid://shopify")
                          ? parseGid(line.id).id
                          : line.id
                      )
                      .join(";"),
                  },
                ]
              : []),
          ],
          merchandiseId: getShopifyGlobalId("ProductVariant", line.id),
          quantity: line.quantity,
        };
      });

      setLinesToAdd(linesToAdd);
    },
    [intelligemsEnabled]
  );

  /**
   * This effect is responsible for initializing the cart
   * It runs when new lines are queued to be added to the cart via the handleAddToCart function
   * It also attaches custom attributes to the cart if necessary
   */
  useEffect(() => {
    if (!linesToAdd || (status !== "uninitialized" && status !== "idle")) {
      return;
    }

    if (!intelligemsEnabled || !window.igData?.user?.igId) {
      linesAdd(linesToAdd);
      addToLegacyCart(linesToAdd);
      setLinesToAdd(undefined);
      return;
    }

    const activeExperiment = window.igData?.user.getExperiments()?.[0];

    const attributesToUpdate = [
      {
        key: "igId",
        value: window.igData.user.igId,
      },
      ...(activeExperiment
        ? [
            {
              key: "igPreview",
              value: String(activeExperiment.isPreview),
            },
          ]
        : []),
    ];

    if (status === "uninitialized") {
      cartCreate({
        attributes: attributesToUpdate,
        lines: linesToAdd,
      });
    } else if (status === "idle") {
      linesAdd(linesToAdd);
      addToLegacyCart(linesToAdd);
    }

    setLinesToAdd(undefined);
  }, [
    addToLegacyCart,
    cartCreate,
    intelligemsEnabled,
    linesAdd,
    linesToAdd,
    status,
  ]);

  /**
   * This effect is responsible for updating the cart item count
   */
  useEffect(() => {
    if (!Array.isArray(lines)) {
      return;
    }

    TablescapeContext.emit(TablescapeEvent.UPDATE_CART_ITEM_COUNT, {
      cart: {
        lines,
        quantity: lines.reduce(
          (total, line) => total + (line?.quantity ?? 0),
          0
        ),
      },
    });
  }, [lines]);

  /**
   * This effect binds the handleAddToCart function to TablescapeContext
   * It is responsible for listening for external ADD_TO_CART and ADD_BUNDLE_TO_CART events
   */
  useEffect(() => {
    const addToCartListener = TablescapeContext.on(
      TablescapeEvent.ADD_TO_CART,
      handleAddToCart
    );
    const addBundleToCartListener = TablescapeContext.on(
      TablescapeEvent.ADD_BUNDLE_TO_CART,
      handleAddToCart
    );

    return () => {
      addToCartListener.off(TablescapeEvent.ADD_TO_CART, handleAddToCart);
      addBundleToCartListener.off(
        TablescapeEvent.ADD_BUNDLE_TO_CART,
        handleAddToCart
      );
    };
  }, [handleAddToCart]);

  /**
   * This effect is responsible for updating the cart id in TablescapeContext
   */
  useEffect(() => {
    if (!id) {
      return;
    }

    TablescapeContext.emit(TablescapeEvent.UPDATE_CART_ID, {
      cart: {
        id,
      },
    });
  }, [id]);

  /**
   * This effect is responsible for tying the legacy cart to the Storefront cart
   */
  useEffect(() => {
    if (id && status === "idle" && legacyCartStatus === "idle") {
      const storefrontCartId = parseGid(id).id;

      if (legacyCartAttributes?.storefront_cart_id !== storefrontCartId)
        updateToLegacyCart({
          attributes: {
            storefront_cart_id: storefrontCartId,
          },
        });
    }
  }, [id, legacyCartAttributes, legacyCartStatus, status, updateToLegacyCart]);

  /**
   * This effect is responsible for clearing the legacy cart when the new cart is empty
   */
  useEffect(() => {
    if (
      legacyCartStatus === "idle" &&
      status === "idle" &&
      lines?.length === 0 &&
      legacyCartItems?.length
    ) {
      clearLegacyCart();
    }
  }, [
    clearLegacyCart,
    legacyCartItems?.length,
    legacyCartStatus,
    lines?.length,
    status,
  ]);
}
